import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { API_URL } from "../config/api";
import { Loader2, Send, Users, TrendingUp, ThumbsUp, ThumbsDown, Minus, Power } from "lucide-react";

const npsColor = (n) => n < 0 ? "text-red-600" : n < 30 ? "text-amber-600" : n < 50 ? "text-lime-600" : "text-emerald-600";

export default function NPSPage() {
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [pfInput, setPfInput] = useState("");
  const [emailsInput, setEmailsInput] = useState("");
  const [surveyType, setSurveyType] = useState("relationship");
  const [sending, setSending] = useState(false);
  const [bulkDays, setBulkDays] = useState(30);
  const [bulkSending, setBulkSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([
        axios.get(`${API_URL}/nps/dashboard`, auth),
        axios.get(`${API_URL}/nps/settings`, auth),
      ]);
      setData(d.data);
      setSettings(s.data);
    } catch (e) {
      toast.error("Failed to load NPS dashboard");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const toggleLive = async (val) => {
    try {
      const r = await axios.put(`${API_URL}/nps/settings`, { live_mode: val }, auth);
      setSettings(r.data);
      toast.success(val ? "LIVE mode ON — surveys now go to real customers" : "TEST mode — surveys redirect to your test email");
    } catch { toast.error("Could not update settings"); }
  };

  const updateTestEmail = async (email) => {
    try {
      const r = await axios.put(`${API_URL}/nps/settings`, { test_email: email }, auth);
      setSettings(r.data);
      toast.success("Test email updated");
    } catch { toast.error("Could not update"); }
  };

  const send = async () => {
    const emails = emailsInput.split(/[,;\n]+/).map((e) => e.trim()).filter(Boolean);
    if (!pfInput.trim() && emails.length === 0) { toast.error("Enter a PF number or customer email(s)"); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API_URL}/nps/send`, {
        pf_id: pfInput.trim() || null,
        emails: emails.length ? emails : null,
        survey_type: surveyType,
      }, auth);
      toast.success(r.data.message);
      setPfInput(""); setEmailsInput("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const sendBulk = async () => {
    const days = Math.max(1, parseInt(bulkDays, 10) || 30);
    setBulkSending(true);
    try {
      const preview = await axios.post(
        `${API_URL}/nps/send-bulk?survey_type=${surveyType}&since_days=${days}&basis=created&dry_run=true`,
        {}, auth);
      const { unique_contacts, matched_pfs, live_mode } = preview.data;
      if (!unique_contacts) {
        toast.info(`No customer contacts found on shipments opened in the last ${days} days.`);
        return;
      }
      const warn = live_mode ? "LIVE — these go to REAL customers." : "TEST mode — all redirected to your test email.";
      if (!window.confirm(
        `Send a ${surveyType} NPS survey to ${unique_contacts} customer(s) from ${matched_pfs} shipment(s) opened in the last ${days} days?\n\n${warn}\n(Anyone surveyed in the last ${settings?.suppression_days} days is skipped automatically.)`)) {
        return;
      }
      const r = await axios.post(
        `${API_URL}/nps/send-bulk?survey_type=${surveyType}&since_days=${days}&basis=created`,
        {}, auth);
      toast.success(r.data.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk send failed");
    } finally { setBulkSending(false); }
  };

  if (loading) return (<div><Navbar /><div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div></div>);

  const Stat = ({ icon: Icon, label, value, sub, color }) => (
    <div className="flex-1 bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 text-slate-500 text-sm mb-1"><Icon className="h-4 w-4" />{label}</div>
      <div className={`text-3xl font-bold ${color || "text-slate-800"}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="nps-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Customer NPS</h1>
            <p className="text-slate-500 text-sm">Net Promoter Score — how likely customers are to recommend Europacific.</p>
          </div>
          <div className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 px-4 py-2.5" data-testid="nps-live-toggle">
            <Power className={`h-4 w-4 ${settings?.live_mode ? "text-emerald-600" : "text-slate-400"}`} />
            <span className="text-sm font-medium text-slate-700">{settings?.live_mode ? "LIVE" : "TEST mode"}</span>
            <Switch checked={!!settings?.live_mode} onCheckedChange={toggleLive} data-testid="nps-live-switch" />
          </div>
        </div>

        {!settings?.live_mode && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex flex-wrap items-center gap-2 text-sm text-amber-800" data-testid="nps-test-banner">
            <span>TEST mode — all surveys are delivered to</span>
            <Input value={settings?.test_email || ""} onBlur={(e) => updateTestEmail(e.target.value)}
              onChange={(e) => setSettings({ ...settings, test_email: e.target.value })}
              className="h-7 w-64 bg-white" data-testid="nps-test-email" />
            <span>instead of real customers. Flip to LIVE when ready.</span>
          </div>
        )}

        {/* Headline */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col sm:flex-row items-center gap-6">
          <div className="text-center">
            <div className={`text-6xl font-black ${npsColor(data.nps)}`} data-testid="nps-score">{data.nps}</div>
            <div className="text-sm text-slate-500 mt-1">NPS score</div>
            <Badge className="mt-2" variant="secondary">{data.band}</Badge>
          </div>
          <div className="flex-1 grid grid-cols-3 gap-3 w-full">
            <div className="text-center bg-emerald-50 rounded-lg p-3"><ThumbsUp className="h-4 w-4 text-emerald-600 mx-auto mb-1" /><div className="text-2xl font-bold text-emerald-700">{data.promoters_pct}%</div><div className="text-xs text-slate-500">Promoters (9-10) · {data.promoters}</div></div>
            <div className="text-center bg-amber-50 rounded-lg p-3"><Minus className="h-4 w-4 text-amber-600 mx-auto mb-1" /><div className="text-2xl font-bold text-amber-700">{data.passives_pct}%</div><div className="text-xs text-slate-500">Passives (7-8) · {data.passives}</div></div>
            <div className="text-center bg-red-50 rounded-lg p-3"><ThumbsDown className="h-4 w-4 text-red-600 mx-auto mb-1" /><div className="text-2xl font-bold text-red-700">{data.detractors_pct}%</div><div className="text-xs text-slate-500">Detractors (0-6) · {data.detractors}</div></div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Stat icon={Users} label="Responses" value={data.total_responses} sub={`${data.sent_total} sent`} />
          <Stat icon={TrendingUp} label="Response rate" value={`${data.response_rate}%`} />
          <Stat icon={Send} label="Surveys sent" value={data.sent_total} />
        </div>

        {/* Send */}
        <Card data-testid="nps-send-card">
          <CardHeader><CardTitle className="text-lg">Send a survey</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <button onClick={() => setSurveyType("relationship")} data-testid="nps-type-relationship"
                className={`px-3 py-1.5 rounded-lg text-sm border ${surveyType === "relationship" ? "bg-[#003366] text-white border-[#003366]" : "bg-white text-slate-600 border-slate-300"}`}>Relationship (quarterly)</button>
              <button onClick={() => setSurveyType("transactional")} data-testid="nps-type-transactional"
                className={`px-3 py-1.5 rounded-lg text-sm border ${surveyType === "transactional" ? "bg-[#003366] text-white border-[#003366]" : "bg-white text-slate-600 border-slate-300"}`}>Transactional (after shipment)</button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">PF number (pulls its customer contacts)</label>
                <Input value={pfInput} onChange={(e) => setPfInput(e.target.value)} placeholder="e.g. PF-000615-26" data-testid="nps-send-pf" />
              </div>
              <div>
                <label className="text-xs text-slate-500">…or customer email(s), comma-separated</label>
                <Input value={emailsInput} onChange={(e) => setEmailsInput(e.target.value)} placeholder="customer@example.com" data-testid="nps-send-emails" />
              </div>
            </div>
            <Button onClick={send} disabled={sending} data-testid="nps-send-btn" className="bg-[#003366] hover:bg-[#004488]">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Send NPS survey</>}
            </Button>
            <p className="text-xs text-slate-400">Internal addresses (europacific.com, partner.europacific.com, cargo-cfs.com, epholding.com) are automatically excluded. A per-customer fatigue guard skips anyone surveyed in the last {settings?.suppression_days} days.</p>

            <div className="border-t border-slate-100 pt-3 mt-1">
              <label className="text-xs font-medium text-slate-600">No PF needed — survey recent customers</label>
              <p className="text-xs text-slate-400 mb-2">Pulls the customer contacts already stored on shipments opened recently. You don't enter anything.</p>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-slate-500">Opened in the last</span>
                <Input type="number" min={1} value={bulkDays} onChange={(e) => setBulkDays(e.target.value)}
                  className="h-9 w-20" data-testid="nps-bulk-days" />
                <span className="text-sm text-slate-500">days</span>
                <Button onClick={sendBulk} disabled={bulkSending} variant="outline"
                  data-testid="nps-bulk-send-btn" className="border-[#003366] text-[#003366] hover:bg-slate-50">
                  {bulkSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Users className="h-4 w-4 mr-1.5" /> Send to recent customers</>}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Trend + breakdowns */}
        <div className="grid lg:grid-cols-2 gap-4">
          <Card data-testid="nps-trend"><CardHeader><CardTitle className="text-base">NPS trend</CardTitle></CardHeader>
            <CardContent>
              {data.trend.length === 0 ? <p className="text-sm text-slate-400">No responses yet.</p> : (
                <div className="space-y-2">
                  {data.trend.map((t) => (
                    <div key={t.month} className="flex items-center gap-3 text-sm">
                      <span className="w-16 text-slate-500">{t.month}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-3 relative">
                        <div className={`h-3 rounded-full ${t.nps < 0 ? "bg-red-400" : t.nps < 30 ? "bg-amber-400" : "bg-emerald-500"}`} style={{ width: `${Math.max(4, (t.nps + 100) / 2)}%` }} />
                      </div>
                      <span className={`w-10 text-right font-semibold ${npsColor(t.nps)}`}>{t.nps}</span>
                      <span className="w-14 text-right text-xs text-slate-400">{t.responses} resp</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="nps-by-service"><CardHeader><CardTitle className="text-base">NPS by service</CardTitle></CardHeader>
            <CardContent>
              {data.by_service.length === 0 ? <p className="text-sm text-slate-400">No data yet.</p> : (
                <div className="space-y-1.5">
                  {data.by_service.map((r) => (
                    <div key={r.label} className="flex justify-between text-sm border-b border-slate-100 py-1">
                      <span className="text-slate-600">{r.label}</span>
                      <span className={`font-semibold ${npsColor(r.nps)}`}>{r.nps} <span className="text-slate-400 font-normal">({r.responses})</span></span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="nps-by-staff"><CardHeader><CardTitle className="text-base">NPS by sales rep / owner</CardTitle></CardHeader>
            <CardContent>
              {data.by_staff.length === 0 ? <p className="text-sm text-slate-400">No data yet.</p> : (
                <div className="space-y-1.5">
                  {data.by_staff.map((r) => (
                    <div key={r.label} className="flex justify-between text-sm border-b border-slate-100 py-1">
                      <span className="text-slate-600 truncate">{r.label}</span>
                      <span className={`font-semibold ${npsColor(r.nps)}`}>{r.nps} <span className="text-slate-400 font-normal">({r.responses})</span></span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="nps-aspects"><CardHeader><CardTitle className="text-base">Most important aspects</CardTitle></CardHeader>
            <CardContent>
              {data.top_aspects.length === 0 ? <p className="text-sm text-slate-400">No data yet.</p> : (
                <div className="flex flex-wrap gap-2">
                  {data.top_aspects.map((a) => (
                    <Badge key={a.aspect} variant="secondary" className="text-xs">{a.aspect} · {a.count}</Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
